import React, { Component } from 'react';
import { Flex, Heading, Icon, Spinner, Text, Collapse } from 'core/components';
import { observer } from 'mobx-react';
import { TbLayoutSidebar } from 'react-icons/tb';
import { ToolResultCard, InlineToolResultContainer } from './KaizenChatSession.styles';
import { createToolResultComponent, hasToolResultView } from './ToolResultFactory';

@observer
class KaizenToolResult extends Component {
  _flashTimeout = null;

  state = {
    shouldFlash: false,
    isExpanded: false
  };

  componentDidUpdate(prevProps, prevState) {
    const { tool, step, isExistingChatLoading } = this.props;
    const prevStatus = prevProps.tool?.status;
    const currStatus = tool?.status;

    // Flash logic
    if ((!prevStatus || prevStatus === 'PROCESSING') && currStatus === 'SUCCESS' && !prevState.shouldFlash) {
      clearTimeout(this._flashTimeout);
      setTimeout(() => {
        this.setState({ shouldFlash: true });
        this._flashTimeout = setTimeout(() => {
          this.setState({ shouldFlash: false });
        }, 900);
      }, 0);
    }

    // as tools inside a step complete, we want to call the onSelectToolResult callback
    if (!isExistingChatLoading && step && step.toolCalls) {
      const currToolCalls = step.toolCalls || [];
      const { onSelectToolResult } = this.props;
      currToolCalls.forEach((currTool) => {
        if (currTool && currTool.status === 'SUCCESS') {
          if (onSelectToolResult) {
            onSelectToolResult(currTool);
          }
        }
      });
    }
  }

  componentWillUnmount() {
    clearTimeout(this._flashTimeout);
  }

  handleToggleExpanded = () => {
    this.setState((prevState) => ({
      isExpanded: !prevState.isExpanded
    }));
  };

  handleClick = () => {
    const { tool, onSelectToolResult, isCopilotMode } = this.props;
    const hasDetailView = hasToolResultView.includes(tool.tool_name);

    if (hasDetailView) {
      if (isCopilotMode) {
        // In copilot mode, toggle inline expansion
        this.handleToggleExpanded();
      } else {
        // In normal mode, open in side panel
        onSelectToolResult(tool);
      }
    }
  };

  render() {
    const { tool, isCopilotMode, onDataViewCreate, onQueryError } = this.props;
    const { shouldFlash, isExpanded } = this.state;

    if (!tool) {
      return null;
    }

    const { title, status } = tool;

    const hasDetailView = hasToolResultView.includes(tool.tool_name);
    const isInteractive = hasDetailView && status === 'SUCCESS';

    const headerProps = {
      color: !status ? 'muted' : 'body'
    };

    let cardClass = status ? status.toLowerCase() : '';
    if (status === 'SUCCESS' && shouldFlash) {
      cardClass += ' flash';
    }

    const toolResultCard = (
      <ToolResultCard
        interactive={isInteractive}
        elevation={isInteractive ? 2 : 1}
        className={cardClass}
        onClick={this.handleClick}
      >
        <Flex alignItems="center" gap={1} flex={1}>
          {!status && <Spinner size={12} />}
          {status === 'FAILED' && <Icon icon="error" color="danger" />}
          {status === 'SUCCESS' && <Icon icon="tick-circle" color="success" className="success-icon" />}
          <Heading level={6} m={0} fontWeight={500} {...headerProps}>
            {title}
          </Heading>
        </Flex>

        {status === 'FAILED' && (
          <Text small color="danger">
            <Text fontWeight="bold">Error:</Text> Something went wrong
          </Text>
        )}

        {status === 'SUCCESS' && hasDetailView && (
          <Flex alignItems="center" gap="6px">
            <Text muted small>
              {(() => {
                if (isCopilotMode) {
                  return isExpanded ? 'Collapse' : 'Expand';
                }
                return 'View details';
              })()}
            </Text>
            <Icon
              icon={(() => {
                if (isCopilotMode) {
                  return isExpanded ? 'caret-up' : 'caret-down';
                }
                return TbLayoutSidebar;
              })()}
              size={14}
              color="muted"
            />
          </Flex>
        )}
      </ToolResultCard>
    );

    // In copilot mode, show inline expansion
    if (isCopilotMode && hasDetailView && status === 'SUCCESS') {
      return (
        <Flex flexDirection="column" gap={1}>
          {toolResultCard}
          {isExpanded && (
            <Collapse isOpen={isExpanded}>
              <InlineToolResultContainer>
                {createToolResultComponent(tool, {
                  onCloseToolResult: () => this.setState({ isExpanded: false }),
                  onDataViewCreate,
                  onQueryError,
                  chartHeight: 300, // Smaller height for inline mode
                  isInlineMode: true
                })}
              </InlineToolResultContainer>
            </Collapse>
          )}
        </Flex>
      );
    }

    // In normal mode, just show the card
    return toolResultCard;
  }
}

export default KaizenToolResult;
